import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Avatar,
  CircularProgress,
} from "@mui/material";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "../../styles/slick-theme.css";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import StarIcon from "@mui/icons-material/Star";
import { useProperty } from "../../context/PropertyContext";
import { useNavigate } from "react-router-dom";
import { useCurrency } from "../../context/CurrencyContext";
import { PropertyListing } from "../../types/Property";
import { useAuth } from "../../context/AuthContext";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import { iconMapping } from "./../Header";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Download } from "@mui/icons-material";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

interface PropertyCardProps {
  property: PropertyListing;
  isMapView?: boolean;
  onConvertPrice: (price: number, currency: string) => void;
  fromBrokerProfile?: boolean;
  hunterFullName?: string | null;
  hunterProfileImage?: string | null;
}

const PropertyCard: React.FC<PropertyCardProps> = ({
  property,
  isMapView = false,
  onConvertPrice,
  fromBrokerProfile = false,
  hunterFullName,
  hunterProfileImage,
}) => {
  const [showStar, setShowStar] = useState(false);
  const [canSlidePrev, setCanSlidePrev] = useState(false);
  const [canSlideNext, setCanSlideNext] = useState(property.photos.length > 1);
  const sliderRef = useRef<Slider>(null);
  const { favorites, toggleFavorite, category } = useProperty();
  const navigate = useNavigate();
  const { selectedCurrency, sellRate, buyRate } = useCurrency();
  const { tokenKey, isHunter, isBroker } = useAuth();
  const [currentIndex, setCurrentIndex] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    setShowStar(category === "Novedad");
  }, [category]);

  const handleDownloadPDF = async (event: React.MouseEvent) => {
    event.stopPropagation();

    try {
      if (!cardRef.current) return;

      setIsDownloading(true);

      // Pequeña pausa para asegurar renderizado completo
      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = await html2canvas(cardRef.current, {
        useCORS: true,
        allowTaint: true,
        scale: 2,
        backgroundColor: '#ffffff',
        logging: true,
      });

      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgData = canvas.toDataURL('image/png');

      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`${property.title}.pdf`);

    } catch (error) {
      console.error('Error al generar PDF:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePriceConversion = () => {
    let newPrice = property.price;
    let newCurrency = property.currency; // Empezamos con la moneda actual de la propiedad

    if (selectedCurrency === "MIXTO") {
      // Si es MIXTO, devolvemos el precio y la moneda original de la propiedad
      return { price: newPrice, currency: property.currency };
    }

    if (selectedCurrency === "CRC") {
      if (property.currency === "CRC") {
        // Si ya está en CRC, no se convierte
        return { price: newPrice, currency: newCurrency };
      } else if (buyRate) {
        // Convertimos a CRC usando buyRate
        newPrice = property.price * buyRate;
        newCurrency = "CRC"; // Actualizamos la moneda convertida
        return { price: newPrice, currency: newCurrency };
      }
    }

    if (selectedCurrency === "USD") {
      if (property.currency === "USD") {
        // Si ya está en USD, no se convierte
        return { price: newPrice, currency: newCurrency };
      } else if (sellRate) {
        // Convertimos a USD usando sellRate
        newPrice = property.price / sellRate;
        newCurrency = "USD"; // Actualizamos la moneda convertida
        return { price: newPrice, currency: newCurrency };
      }
    }

    return { price: newPrice, currency: newCurrency }; // Si no se cumplen las condiciones, retornamos el precio original y la moneda original
  };

  // Luego, en tu JSX:

  const { price: convertedPrice, currency: convertedCurrency } =
    handlePriceConversion();

  const formatPrice = (price: number, propertyCurrency: string) => {
    let locale = "es-CR";
    let symbol = "";

    switch (propertyCurrency) {
      case "CRC":
        locale = "es-CR";
        symbol = "₡";
        break;
      case "USD":
        locale = "en-US";
        symbol = "$";
        break;
      default:
        locale = "en-US";
        symbol = "$";
    }

    return `${symbol}${price.toLocaleString(locale)} ${propertyCurrency}`;
  };

  function formatDate(date: string | number | Date): string {
    return new Date(date).toLocaleDateString("es-CR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  }

  const formattedUpdatedDate = formatDate(property.updatedDate);
  const formattedCreatedDate = formatDate(property.createdDate);

  const isFavorite = favorites.has(property.id_Property);

  const handleClick = () => {
    navigate(`/properties/property-detail/${property.id_Property}`, {
      state: { fromBrokerProfile: fromBrokerProfile },
    });
  };

  const handleClickNext = (event: React.MouseEvent) => {
    event.stopPropagation();
    sliderRef.current?.slickNext();
  };

  const handleClickPrev = (event: React.MouseEvent) => {
    event.stopPropagation();
    sliderRef.current?.slickPrev();
  };

  const handleAfterChange = (currentSlide: number) => {
    if (sliderRef.current) {
      const totalSlides = property.photos.length;
      setCanSlidePrev(currentSlide > 0);
      setCanSlideNext(currentSlide < totalSlides - 1);
      setCurrentIndex(currentSlide);
    }
  };

  const sliderSettings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    afterChange: handleAfterChange,
    appendDots: (dots: any) => {
      const visibleDots = 5;
      const totalDots = dots.length;

      let start = Math.max(0, currentIndex - Math.floor(visibleDots / 2));
      let end = start + visibleDots;

      if (end > totalDots) {
        end = totalDots;
        start = Math.max(0, end - visibleDots);
      }

      const slicedDots = dots.slice(start, end);

      return <ul className="slick-dots">{slicedDots}</ul>;
    },
  };

  const handleFavorite = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (tokenKey) {
      toggleFavorite(property.id_Property);
    }
  };

  const isValidUrl = (urlString: string): boolean => {
    try {
      const url = new URL(urlString);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch (_) {
      return false;
    }
  };

  const handleSocialClick = (
    e: React.MouseEvent,
    url: string | undefined | null,
    defaultUrl: string
  ): void => {
    e.stopPropagation();

    let finalUrl = defaultUrl;

    if (url) {
      try {
        const formattedUrl = url.startsWith("http") ? url : `https://${url}`;
        if (isValidUrl(formattedUrl)) {
          finalUrl = formattedUrl;
        } else {
          console.error(`URL no válida: ${formattedUrl}`);
        }
      } catch (error) {
        console.error(`Error al procesar URL (${url}):`, error);
      }
    }

    window.open(finalUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Card
      ref={cardRef}
      onClick={
        category !== "No disponible" &&
          property.moduleStateId === 1 &&
          property.statusCode !== "Vendido" &&
          property.statusCode !== "Alquilado"
          ? handleClick
          : undefined
      }
      sx={{
        // width: "100%",
        ...(isMapView ? { mt: 3 } : { m: 0 }),
        borderRadius: 2,
        boxShadow: { xs: "none", sm: 3 },
        cursor:
          category !== "No disponible" &&
            property.moduleStateId === 1 &&
            property.statusCode !== "Vendido" &&
            property.statusCode !== "Alquilado"
            ? "pointer"
            : "default",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        position: "relative",
        backgroundColor: { xs: "transparent", sm: "background.paper" },
        "&:hover": {
          boxShadow: { xs: "none", sm: 6 },
        },
      }}
    >
      {(property.statusCode === "Vendida" ||
        property.statusCode === "Alquilada") && (
          <>
            {/* Cinta roja centrada */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1001,
                pointerEvents: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  transform: "rotate(-45deg)",
                  backgroundColor: "rgba(244, 67, 54, 0.9)", // rojo
                  color: "white",
                  padding: "8px 40px",
                  fontSize: "1.2rem",
                  fontWeight: "bold",
                  letterSpacing: "2px",
                  borderRadius: "4px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                  minWidth: "150%",
                  textAlign: "center",
                  pointerEvents: "auto",
                }}
              >
                {property.statusCode.toUpperCase()} por {property.soldByFullName}
              </Box>
            </Box>
            <Box
              sx={{
                position: "absolute",
                top: 30,
                left: -80,
                zIndex: 1001,
                pointerEvents: "none",
                width: "100%",
                maxWidth: "100%",
              }}
            >
              <Box
                sx={{
                  transform: "rotate(-40deg)",
                  backgroundColor: "black",
                  color: "white",
                  padding: "4px 12px",
                  fontSize: "0.8rem",
                  fontWeight: "bold",
                  letterSpacing: "1px",
                  borderRadius: "3px",
                  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.5)",
                  pointerEvents: "auto",
                  userSelect: "none",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                }}
              >
                NO DISPONIBLE
              </Box>
            </Box>
          </>
        )}

      {/* Lazo de Promoción */}
      {property.promotion && (
        <>
          {property.promotion.discountPercentage !== 0 && (
            <Box
              sx={{
                position: "absolute",
                top: 15,
                right: -23,
                zIndex: 1002,
                pointerEvents: "none",
              }}
            >
              <Box
                sx={{
                  transform: "rotate(45deg)",
                  transformOrigin: "center",
                  backgroundColor: property.promotion.colorTag,
                  color: property.promotion.colorText,
                  padding: "4px 20px",
                  fontSize: "0.7rem",
                  fontWeight: "bold",
                  letterSpacing: "0.5px",
                  borderRadius: "3px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  minWidth: "60px",
                  textTransform: "uppercase",
                }}
              >
                -{property.promotion.discountPercentage}%
              </Box>
            </Box>
          )}

          {property.promotion.title && (
            <Box
              sx={{
                position: "absolute",
                top: 33,
                right: 32,
                zIndex: 1002,
                pointerEvents: "none",
              }}
            >
              <Box
                sx={{
                  transform: "rotate(45deg)",
                  transformOrigin: "center",
                  backgroundColor: "#ffff",
                  color: "black",
                  padding: "4px 20px",
                  fontSize: property.promotion.title.length > 15 ? "0.6rem" : "0.7rem",
                  fontWeight: "bold",
                  letterSpacing: "0.5px",
                  borderRadius: "3px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                  textAlign: "center",
                  width: property.promotion.title.length > 15 ? "158%" : "128%",
                  whiteSpace: "nowrap",
                  textTransform: "uppercase",
                }}
              >
                {property.promotion.title.toUpperCase()}
              </Box>
            </Box>
          )}
        </>
      )}

      <Box
        sx={{
          position: "absolute",
          top: 190,
          right: 0,
          zIndex: 1001,
        }}
      >
        <Tooltip
          title={
            tokenKey &&
              category !== "No disponible" &&
              property.moduleStateId === 1
              ? isFavorite
                ? "Quitar de favoritos"
                : "Añadir a favoritos"
              : ""
          }
        >
          <IconButton
            onClick={
              category !== "No disponible" && property.moduleStateId === 1
                ? handleFavorite
                : undefined
            }
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              zIndex: 1,
              color: "inherit",
              padding: 0,
              transition: "transform 0.2s ease-in-out",
              "&:hover": {
                transform: "scale(1.1)",
              },
            }}
          >
            <img
              src={
                !isFavorite
                  ? "/assets/images/ufonew.png"
                  : "/assets/images/ufonew4.png"
              }
              alt="Favorite Icon"
              style={{ width: "28px", height: "28px" }}
            />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        className="slider-container"
        sx={{
          flex: "0 0 auto",
          height: "240px",
          overflow: "hidden",
          position: "relative",
          borderRadius: { xs: 3, sm: 0 },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 10,
            left: 10,
            backgroundColor: "rgba(255, 255, 255, 0.85)",
            backdropFilter: "blur(8px)",
            padding: "6px 12px",
            borderRadius: "8px",
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            gap: "6px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
          }}
        >
          <Box
            sx={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor:
                property.statusCode !== "Disponible"
                  ? "#FF5252" // Rojo para no disponible
                  : property.modeDescription === "Venta"
                    ? "#4CAF50" // Verde para venta
                    : "#2196F3", // Azul para alquiler
            }}
          />
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              letterSpacing: "0.3px",
              color: "#212121",
              textTransform: "uppercase",
            }}
          >
            {property.statusCode === "Disponible"
              ? `${property.modeDescription}`
              : property.statusCode}
          </Typography>
          {showStar && (
            <StarIcon sx={{ fontSize: "0.9rem", color: "#FFC107" }} />
          )}
        </Box>

        {canSlidePrev && (
          <IconButton
            sx={{
              position: "absolute",
              top: "50%",
              left: 10,
              zIndex: 2,
              color: "black",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 1)",
              },
            }}
            onClick={handleClickPrev}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 15 }} />
          </IconButton>
        )}

        {canSlideNext && (
          <IconButton
            sx={{
              position: "absolute",
              top: "50%",
              right: 10,
              zIndex: 2,
              color: "black",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 1)",
              },
            }}
            onClick={handleClickNext}
          >
            <ArrowForwardIosIcon sx={{ fontSize: 15 }} />
          </IconButton>
        )}

        {/* Cupón de propiedad */}
        {property.coupon && (
          <Box
            sx={{
              position: 'absolute',
              bottom: -12,
              left: -12,
              zIndex: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {/* Título "Cupón" */}
            <Box
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                fontSize: '7px',
                fontWeight: 'bold',
                padding: '1px 6px',
                borderRadius: '6px',
                marginBottom: '-27px',
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
                backdropFilter: 'blur(4px)',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                zIndex: 3,
              }}
            >
              Cupón
            </Box>

            {/* Tag del cupón */}
            <Box sx={{ position: 'relative' }}>
              <Box
                component="img"
                src={property.coupon!.selectedTag}
                alt="Cupón"
                sx={{
                  width: 95,
                  height: 95,
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: '55%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: 'white',
                  fontSize: '8px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)',
                  maxWidth: '50px',
                  lineHeight: 1.1,
                  textTransform: 'uppercase',
                  wordBreak: 'break-word',
                }}
              >
                {property.coupon.title}
                {property.promotion && property.promotion.discountPercentage !== 0 && (
                  <>
                    <br />
                    {property.promotion.discountPercentage}%
                  </>
                )}
              </Box>
            </Box>
          </Box>
        )}

        <Slider {...sliderSettings} ref={sliderRef}>
          {property.photos.map((photo, index) => (
            <Box
              key={index}
              sx={{
                width: "100%",
                height: "240px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#f0f0f0",
              }}
            >
              <Box
                component="img"
                src={photo || "/placeholder.svg"}
                alt={`Vista previa ${index + 1}`}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: { xs: 3, sm: 0 },
                }}
              />
            </Box>
          ))}
        </Slider>
      </Box>

      <CardContent
        sx={{
          flex: "1 1 auto",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-around",
          gap: 1,
          backgroundColor: { xs: "transparent", sm: "background.paper" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: 1.2,
              flex: 1,
            }}
          >
            {property.title}
          </Typography>

          <Box sx={{ display: "flex", gap: 0.5, ml: "auto" }}>
            <Tooltip title="Descargar como PDF">
              <IconButton
              size="small"
              onClick={handleDownloadPDF}
              sx={{
                backgroundColor: "#6CC417",
                "&:hover": {
                backgroundColor: "#5AAE12",
                },
                width: 28,
                height: 28,
                mr: 0.5,
                position: "relative",
              }}
              disabled={isDownloading}
              >
              {isDownloading ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
                <Box sx={{ position: "absolute", top: 5, left: 5 }}>
                  <CircularProgress size={18} color="inherit" />
                </Box>
                </Box>
              ) : (
                <Download sx={{ fontSize: 18, color: "white" }} />
              )}
              </IconButton>
            </Tooltip>
            {/* Botón de Facebook */}
            <Tooltip
              title={property.facebookUrl ? "Facebook" : "No disponible"}
            >
              <span>
                <IconButton
                  size="small"
                  onClick={(e) =>
                    property.facebookUrl &&
                    handleSocialClick(
                      e,
                      property.facebookUrl,
                      "https://www.facebook.com/people/Al%C3%ADen-Realty/61555710022289/"
                    )
                  }
                  disabled={!property.facebookUrl}
                  sx={{
                    backgroundColor: property.facebookUrl
                      ? "#1877F2"
                      : "#e0e0e0",
                    "&:hover": {
                      backgroundColor: property.facebookUrl
                        ? "#166FE5"
                        : "#e0e0e0",
                    },
                    width: 28,
                    height: 28,
                    "&.Mui-disabled": {
                      opacity: 0.7,
                    },
                  }}
                >
                  <img
                    src="/assets/images/facebook2.png"
                    alt="Facebook"
                    style={{
                      width: "22px",
                      height: "22px",
                      filter: property.facebookUrl ? "none" : "grayscale(100%)",
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>

            {/* Botón de Instagram */}
            <Tooltip
              title={property.instagramUrl ? "Instagram" : "No disponible"}
            >
              <span>
                <IconButton
                  size="small"
                  onClick={(e) =>
                    property.instagramUrl &&
                    handleSocialClick(
                      e,
                      property.instagramUrl,
                      "https://www.instagram.com/alienrealty/"
                    )
                  }
                  disabled={!property.instagramUrl}
                  sx={{
                    background: property.instagramUrl
                      ? "linear-gradient(135deg, #FF0099, #7928CA, #FF4500)"
                      : "#e0e0e0",
                    "&:hover": {
                      opacity: property.instagramUrl ? 0.9 : 1,
                    },
                    width: 28,
                    height: 28,
                    "&.Mui-disabled": {
                      opacity: 0.7,
                    },
                  }}
                >
                  <img
                    src="/assets/images/insta-icon.png"
                    alt="Instagram"
                    style={{
                      width: "22px",
                      height: "22px",
                      filter: property.instagramUrl
                        ? "none"
                        : "grayscale(100%)",
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
        <Typography
          variant="subtitle1"
          sx={{
            color: "grey",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {property.address_Description}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            fontWeight: "normal",
            display: "flex",
            alignItems: "center",
            fontSize: "0.8rem",
            color: "grey",
          }}
        >
          Última modificación: {formattedUpdatedDate}
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            {/* Precio original tachado si hay promoción */}
            {property.promotion && property.promotion.discountPercentage !== 0 && (
              <Typography
                variant="body2"
                sx={{
                  textDecoration: "line-through",
                  textDecorationColor: "red",
                  color: "red",
                  fontSize: "0.8rem",
                  fontWeight: "normal",
                }}
              >
                {formatPrice(property.promotion.oldPrice, convertedCurrency)}
              </Typography>
            )}

            {/* Precio actual/oferta */}
            <Typography
              variant="body2"
              sx={{
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                color: property.promotion ? "#4caf50" : "inherit",
                fontSize: property.promotion ? "1rem" : "inherit",
              }}
            >
              {formatPrice(convertedPrice, convertedCurrency)}
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onConvertPrice(convertedPrice, convertedCurrency);
                }}
                size="small"
              >
                <CurrencyExchangeIcon sx={{ fontSize: 20, color: "green" }} />
              </IconButton>
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: "bold", color: "blue", fontSize: "0.65rem" }}
            >
              {property.realtorTeamName || property.realtorFullName}
            </Typography>

            <Avatar
              src={
                property.realtorTeamName
                  ? "/assets/images/profilePicture.png"
                  : property.realtorProfileImage ||
                  "/assets/images/profilePicture.png"
              }
              alt={property.realtorFullName || "Realtor"}
              sx={{ width: 30, height: 30 }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: "normal",
              fontSize: "0.8rem",
              color: "grey",
            }}
          >
            {formattedCreatedDate}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <VisibilityOutlinedIcon fontSize="small" />
            <Typography>{property.viewCount}</Typography>
            {iconMapping[property.categoryName] || iconMapping["No disponible"]}
            <Typography variant="body2" color="textSecondary">
              {property.categoryName}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              #{property.id_Property}
            </Typography>
          </Box>
        </Box>
        {hunterFullName && (isHunter || isBroker) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              mt: 1,
              pt: 1,
              borderTop: "1px solid #eee",
              gap: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.65rem",
                fontStyle: "italic",
                color: "text.secondary",
              }}
            >
              Descubierta por:
            </Typography>

            <Typography
              variant="body2"
              sx={{
                fontWeight: "normal",
                color: "primary.main",
                fontSize: "0.7rem",
              }}
            >
              {hunterFullName}
            </Typography>

            <Avatar
              src={hunterProfileImage || "/assets/images/profilePicture.png"}
              alt={hunterFullName || "Hunter"}
              sx={{ width: 22, height: 22 }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default PropertyCard;
